/**
 * Client Authentication Service
 * Handles email-based authentication for group portal access
 */

export interface ClientAuth {
  id: string;
  email: string;
  name: string;
  isVerified: boolean;
  isPaid: boolean;
  groupId?: string;
  groupName?: string;
  tourName?: string;
  accessToken: string;
  lastLogin: string;
  profilePicture?: string;
  isAnonymous: boolean;
}

export interface GroupAccess {
  groupId: string;
  groupName: string;
  tourName: string;
  startDate: string;
  endDate: string;
  status: 'preparing' | 'active' | 'completed';
  canAccess: boolean;
  accessReason?: string;
}

export class ClientAuthService {
  private static readonly STORAGE_KEY = 'clientAuth';
  private static readonly ACCESS_TOKEN_KEY = 'clientAccessToken';

  /**
   * Authenticate client with email and phone (password)
   */
  static async authenticateWithEmail(email: string, phone: string): Promise<ClientAuth | null> {
    try {
      // Import booking service to check if client has a booking
      const { BookingService } = await import('./bookingService');
      const bookingService = new BookingService();
      
      // Get all bookings for this email
      const bookings = await bookingService.getBookingsByClient(email);
      
      if (!bookings || bookings.length === 0) {
        console.log('No bookings found for email:', email);
        // For testing purposes, allow access with test credentials
        if (email === 'john@example.com' || email === 'sarah@example.com') {
          console.log('Using test credentials for:', email);
          const testClient: ClientAuth = {
            id: `test_${email.split('@')[0]}`,
            email: email,
            name: email === 'john@example.com' ? 'John Doe' : 'Sarah Smith',
            isVerified: true,
            isPaid: true,
            groupId: 'group_001',
            groupName: 'Test Safari Group',
            tourName: 'Maasai Mara Safari',
            accessToken: `test_token_${Date.now()}`,
            lastLogin: new Date().toISOString(),
            isAnonymous: false
          };
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(testClient));
          localStorage.setItem(this.ACCESS_TOKEN_KEY, testClient.accessToken);
          return testClient;
        }
        return null;
      }

      // Find booking with matching phone number (strict matching)
      const booking = bookings.find(b => {
        // Check if phone matches customer phone exactly
        if (b.customer_phone === phone) return true;
        
        // Check if phone matches emergency contact phone exactly
        if (b.emergency_contact_phone === phone) return true;
        
        // Check if phone matches any traveler phone exactly
        if (b.travelers && b.travelers.some((t: any) => t.phone === phone)) return true;
        
        return false;
      });

      if (!booking) {
        console.log('No booking found with matching phone number for email:', email);
        return null;
      }

      // Get the actual group ID from group_members table
      let actualGroupId: string | undefined = undefined;
      
      // First, try to get from booking.group_id if it exists and is a valid UUID
      if (booking.group_id) {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(booking.group_id)) {
          actualGroupId = booking.group_id;
        }
      }
      
      // If not found or invalid, try to get from group_members table
      if (!actualGroupId) {
        try {
          const { supabase } = await import('./supabaseService');
          
          // Validate booking.id is not empty before querying
          if (!booking.id || booking.id.trim() === '') {
            console.warn('Invalid booking ID, cannot fetch group membership');
          } else {
            // Query group_members table (booking_id is now TEXT type after migration)
            const result = await supabase
              .from('group_members')
              .select('group_id')
              .eq('booking_id', booking.id)
              .maybeSingle();
            
            const groupMember = result.data;
            const groupMemberError = result.error;
            
            if (groupMemberError && groupMemberError.code !== 'PGRST116') {
              console.warn('Could not query group_members:', groupMemberError.message);
            }
            
            if (groupMember && groupMember.group_id) {
              // Validate it's a UUID before using it
              const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
              if (uuidRegex.test(groupMember.group_id)) {
                actualGroupId = groupMember.group_id;
              } else {
                console.warn('Group ID from group_members is not a valid UUID:', groupMember.group_id);
              }
            } else if (groupMemberError) {
              // Only log if it's not a "not found" error (which is expected)
              if (groupMemberError.code !== 'PGRST116' && 
                  !groupMemberError.message?.includes('No rows')) {
                console.warn('Error fetching group membership:', groupMemberError.message);
              }
            }
          }
        } catch (error: any) {
          // Silently handle - booking might not have a group yet or database needs migration
          if (error?.code !== 'PGRST116' && 
              !error?.message?.includes('No rows') &&
              error?.status !== 500) {
            console.log('Could not fetch group ID from group_members table:', error?.message || error);
          }
        }
      }
      
      // If still no valid group ID, don't set it (user won't have group access)
      if (!actualGroupId) {
        console.warn('⚠️ No valid group ID found for booking:', booking.id);
        console.warn('💡 This may be because:');
        console.warn('   1. The booking has not been added to a group yet');
        console.warn('   2. The group_members table does not exist or has no matching records');
      }

      // Create client auth object from booking data
      const clientAuth: ClientAuth = {
        id: booking.customer_id || `client_${booking.id}`,
        email: booking.customer_email,
        name: `${booking.customer_first_name} ${booking.customer_last_name}`,
        isVerified: booking.status === 'confirmed',
        isPaid: booking.payment_status === 'completed',
        groupId: actualGroupId, // Will be undefined if not found - this is OK
        groupName: `${booking.tour_name || 'Safari'} Group`,
        tourName: booking.tour_name || 'Safari Tour',
        accessToken: `token_${booking.id}_${Date.now()}`,
        lastLogin: new Date().toISOString(),
        isAnonymous: false
      };

      // Store authentication data
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(clientAuth));
      localStorage.setItem(this.ACCESS_TOKEN_KEY, clientAuth.accessToken);
      return clientAuth;
    } catch (error) {
      console.error('Authentication failed:', error);
      return null;
    }
  }

  /**
   * Check if client has any bookings (to show login button)
   */
  static async hasBookings(email: string): Promise<boolean> {
    try {
      const { BookingService } = await import('./bookingService');
      const bookingService = new BookingService();
      
      const bookings = await bookingService.getBookingsByClient(email);
      
      // For testing purposes, always return true for test emails
      if (email === 'john@example.com' || email === 'sarah@example.com') {
        return true;
      }
      
      return bookings && bookings.length > 0;
    } catch (error) {
      console.error('Failed to check bookings:', error);
      // For testing purposes, allow test emails even if booking check fails
      if (email === 'john@example.com' || email === 'sarah@example.com') {
        return true;
      }
      return false;
    }
  }

  /**
   * Get current authenticated client
   */
  static getCurrentClient(): ClientAuth | null {
    try {
      const authData = localStorage.getItem(this.STORAGE_KEY);
      if (authData) {
        return JSON.parse(authData);
      }
      return null;
    } catch (error) {
      console.error('Failed to get current client:', error);
      return null;
    }
  }

  /**
   * Check if client has portal access
   */
  static hasPortalAccess(): boolean {
    const client = this.getCurrentClient();
    return client ? client.isVerified && client.isPaid && !!client.groupId : false;
  }

  /**
   * Get group access information
   */
  static getGroupAccess(): GroupAccess | null {
    const client = this.getCurrentClient();
    if (!client || !client.groupId) return null;

    return {
      groupId: client.groupId,
      groupName: client.groupName || '',
      tourName: client.tourName || '',
      startDate: '2024-02-15',
      endDate: '2024-02-18',
      status: 'preparing',
      canAccess: client.isVerified && client.isPaid,
      accessReason: client.isVerified && client.isPaid ? 'Access granted' : 'Payment required'
    };
  }

  /**
   * Logout client
   */
  static logout(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Update client profile
   */
  static updateProfile(updates: Partial<ClientAuth>): boolean {
    try {
      const client = this.getCurrentClient();
      if (client) {
        const updatedClient = { ...client, ...updates };
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedClient));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to update profile:', error);
      return false;
    }
  }

  /**
   * Check if client is anonymous
   */
  static isAnonymous(): boolean {
    const client = this.getCurrentClient();
    return client ? client.isAnonymous : true;
  }

  /**
   * Toggle anonymous mode
   */
  static toggleAnonymousMode(): boolean {
    const client = this.getCurrentClient();
    if (client) {
      return this.updateProfile({ isAnonymous: !client.isAnonymous });
    }
    return false;
  }
}



